import { UIController } from '@/game/GameComponents/PhaserGame/UI/UIController';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import {
  DoPositiveFeedback,
  shouldSubmitOnPointerUp,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { HOVER_CURSOR_PATH } from '@/game/GameComponents/Utils/GameSpriteAssetsMap';
import Checkbox from '@/game/prefabs/GeneralUI/Checkbox';
import CheckboxSmall from '@/game/prefabs/GeneralUI/CheckboxSmall';

export class CheckboxController extends UIController {
  constructor(checkbox: CheckboxType) {
    super(checkbox.scene, checkbox);
    this.checkbox = checkbox;

    checkbox.checkboxBacking.setInteractive({
      cursor: `${HOVER_CURSOR_PATH}, auto`,
    });
    checkbox.checkboxBacking.on(
      'pointerup',
      (e: any) => {
        if (shouldSubmitOnPointerUp(e)) {
          this.handleUserSelection();
        }
      },
      this,
    );
    checkbox.checkboxBacking.on(
      'pointerover',
      () => this.handleUserHoverOn(),
      this,
    );
    checkbox.checkboxBacking.on(
      'pointerout',
      () => this.handleUserHoverOff(),
      this,
    );
  }

  isSelected: boolean = false;
  private checkbox: CheckboxType;

  onSelectionChanged: Array<(newSelection: boolean) => void> = [];

  handleUserSelection() {
    PhaserAudioManager.playSFX(this.checkbox.scene, 'submit-click');
    this.setSelected(!this.isSelected, false, true);
    this.onSelectionChanged.forEach((fn) => fn(this.isSelected));
  }

  setSelected(
    isSelected: boolean,
    doFeedback: boolean = false,
    cursorOver: boolean = false,
  ) {
    if (!this.isSelected && isSelected && doFeedback) {
      DoPositiveFeedback(this.checkbox.scene, this.checkbox, {
        feedbackDuration: 200,
        heightModifier: 0.5,
      });
      PhaserAudioManager.playSFX(this.checkbox.scene, 'rule-complete-sfx');
    }
    this.isSelected = isSelected;
    if (this.checkbox.IsInteractive) {
      this.checkbox.checkboxBacking.setTexture(
        isSelected
          ? cursorOver
            ? this.checkbox.hoverOnTexture
            : this.checkbox.onTexture
          : cursorOver
            ? this.checkbox.hoverOffTexture
            : this.checkbox.offTexture,
      );
    } else {
      this.checkbox.checkboxBacking.setTexture(
        isSelected
          ? this.checkbox.disabledOnTexture
          : this.checkbox.disabledOffTexture,
      );
    }
  }

  setIsInteractive(isInteractive: boolean) {
    const { checkbox } = this;
    checkbox.IsInteractive = isInteractive;
    if (isInteractive) {
      checkbox.checkboxBacking.setInteractive({
        cursor: `${HOVER_CURSOR_PATH}, auto`,
      });
      this.checkbox.checkboxBacking.setTexture(
        this.isSelected ? this.checkbox.onTexture : this.checkbox.offTexture,
      );
    } else {
      checkbox.checkboxBacking.disableInteractive();
      checkbox.checkboxBacking.setTexture(
        this.isSelected
          ? this.checkbox.disabledOnTexture
          : this.checkbox.disabledOffTexture,
      );
    }
  }

  correlateWith(otherCheckbox: CheckboxType) {
    this.onSelectionChanged.push((newSelection: boolean) => {
      otherCheckbox.brain.setSelected(newSelection);
    });
  }

  handleUserHoverOn() {
    if (!this.checkbox.IsInteractive) return;
    this.checkbox.checkboxBacking.setTexture(
      this.isSelected
        ? this.checkbox.hoverOnTexture
        : this.checkbox.hoverOffTexture,
    );
  }

  handleUserHoverOff() {
    if (!this.checkbox.IsInteractive) return;
    this.checkbox.checkboxBacking.setTexture(
      this.isSelected ? this.checkbox.onTexture : this.checkbox.offTexture,
    );
  }
}

export type CheckboxType = Checkbox | CheckboxSmall;
